import { useEffect, useRef } from 'react';

export default function ParticleBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    // Respect users who ask for less motion — render a single static frame instead
    // of a perpetual animation loop.
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Set canvas dimensions to match window
    const setCanvasDimensions = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    setCanvasDimensions();
    window.addEventListener('resize', setCanvasDimensions);

    // Particle properties
    const particleCount = 50;
    const particles = [];

    // Create particles
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        radius: Math.random() * 2 + 0.5,
        color: getRandomColor(),
        speedX: Math.random() * 0.5 - 0.25,
        speedY: Math.random() * 0.5 - 0.25,
        directionChangeTimer: 0,
        directionChangeCooldown: Math.random() * 200 + 50
      });
    }

    function getRandomColor() {
      const colors = [
        'rgba(59, 130, 246, 0.2)', // blue
        'rgba(139, 92, 246, 0.2)',  // purple
        'rgba(79, 70, 229, 0.2)',   // indigo
        'rgba(16, 185, 129, 0.2)'   // green
      ];
      return colors[Math.floor(Math.random() * colors.length)];
    }

    // Render one frame (draw particles, advance their positions, draw connections).
    function draw() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw and update particles
      particles.forEach(particle => {
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        ctx.fillStyle = particle.color;
        ctx.fill();

        // Update position
        particle.x += particle.speedX;
        particle.y += particle.speedY;

        // Change direction occasionally
        particle.directionChangeTimer++;
        if (particle.directionChangeTimer > particle.directionChangeCooldown) {
          particle.speedX = Math.random() * 0.5 - 0.25;
          particle.speedY = Math.random() * 0.5 - 0.25;
          particle.directionChangeTimer = 0;
        }

        // Boundary check - wrap around screen
        if (particle.x < 0) particle.x = canvas.width;
        if (particle.x > canvas.width) particle.x = 0;
        if (particle.y < 0) particle.y = canvas.height;
        if (particle.y > canvas.height) particle.y = 0;
      });

      // Draw connections between close particles
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 150) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(100, 100, 255, ${0.15 * (1 - distance / 150)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
    }

    // Animation loop, with the frame id captured so it can be cancelled on unmount.
    let rafId = null;
    const loop = () => {
      draw();
      rafId = requestAnimationFrame(loop);
    };

    if (reduceMotion) {
      draw(); // single static frame, no loop
    } else {
      loop();
    }

    // Cleanup: MainLayout remounts this on every route change, so without cancelling
    // the frame each navigation would leak another perpetual rAF loop.
    return () => {
      window.removeEventListener('resize', setCanvasDimensions);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed top-0 left-0 w-full h-full -z-10 pointer-events-none"
    />
  );
}
